import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { useRemoveMemberBulkDialogContent } from "@components/table/members/bulk-actions/bulk-remove-member-helper";
import { RemoveMemberBase } from "@components/table/members/bulk-actions/bulk-remove-member-types";
import { BaseMemberAndTeam } from "@custom-types/member-types";
import { removeMemberFromGroup } from "@store/groups/groups-slice-thunk";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedProjectManagerSelector } from "@store/projects/projects-selector";
import { removeMemberFromProject } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import {
  removeMembersByEmails,
} from "@store/teams/teams-slice";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect } from "react";

/** Props for the bulk remove member dialog */
export type BulkRemoveMemberDialogProps = RemoveMemberBase & {
  /** Whether the dialog should be open */
  isOpen: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
};

/**
 * Dialog for removing members in bulk from various contexts (workspace, team, project, group).
 * Handles the confirmation dialog and dispatches the appropriate removal actions.
 */
export function BulkRemoveMemberDialog({
  companyId,
  projectId,
  groupId,
  teamId,
  subjectType,
  onGroupRemoved,
  isOpen,
  onClose,
}: BulkRemoveMemberDialogProps): JSX.Element | null {
  const { user: selectedProjectManager } = useAppSelector(
    selectedProjectManagerSelector
  );

  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );

  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const selectedTeams = useAppSelector(selectedEntitiesSelector("teams"));

  const { bulkDialogContent } = useRemoveMemberBulkDialogContent(subjectType);

  // TODO: https://faro01.atlassian.net/browse/ST-2840 remove this variable after remove the user group on members endpoint
  const selectedEntitiesWithoutTeams = selectedEntities.filter(
    (entities) => entities.kind === "user"
  );

  // Closes the dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog && isOpen) {
      onClose();
    }
  }, [shouldShowSuccessDialog, isOpen, onClose]);

  // Set bulk action name when dialog opens
  useEffect(() => {
    if (isOpen) {
      dispatch(setBulkActionName("removeMember"));

      if (subjectType === "project" && selectedProjectManager) {
        dispatch(
          updateOne({
            id: selectedProjectManager.identity,
            changes: {
              status: "not-allowed",
              message: "Cannot remove owner of project",
            },
          })
        );
      }
    }
  }, [isOpen, subjectType, selectedProjectManager, dispatch]);

  function onRemoveMemberConfirmed(
    member: BaseMemberAndTeam
  ): Promise<unknown> {
    switch (subjectType) {
      case "workspace":
        return dispatch(
          removeMemberFromCompany({
            companyId,
            member,
          })
        )
          .unwrap()
          .then((response) => {
            return dispatch(removeMembersByEmails([response.email]));
          });

      case "workspaceTeam":
        return dispatch(
          removeMemberFromCompany({
            companyId,
            member,
          })
        ).unwrap();

      case "project":
        return dispatch(
          removeMemberFromProject({
            companyId,
            member,
            projectId,
          })
        ).unwrap();

      case "group":
        return dispatch(
          removeMemberFromGroup({
            companyId,
            member,
            groupId,
            onGroupRemoved,
          })
        ).unwrap();

      case "team":
        return dispatch(
          removeMembersFromTeam({
            companyId,
            teamId,
            memberIds: [member.identity],
          })
        ).unwrap();
    }
  }

  function onSuccessfulComplete(): void {
    dispatch(setBulkActionName(null));
    onClose();
  }

  if (!isOpen) {
    return null;
  }

  return (
    <FaroBulkDialog
      dialogContent={bulkDialogContent}
      onClose={onClose}
      trackingEvent={BulkActionEvents.bulkRemoveMember}
      bulkActionCallback={onRemoveMemberConfirmed}
      onSuccessfulComplete={onSuccessfulComplete}
      selectedEntities={[...selectedEntitiesWithoutTeams, ...selectedTeams]}
      uniqueIdKey="identity"
      tableDataType="members"
    />
  );
}
